import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '@org/ui';
import { ArrowRight, Check, Instagram, Linkedin, Minus } from 'lucide-react';
import { rememberReturnTo, sanitizeReturnTo } from '../lib/access';
import { fetchPublicBase, type PublicBase } from '../lib/public-base';
import { formatPrice } from '../components/products/helpers';
import { P, dmSerif, oswald } from '../components/products/theme';
import heroImg from '../assets/landing-hero.webp';
import platformImg from '../assets/landing-platform.webp';
import founderImg from '../assets/landing-mellina.webp';

// PUBLIC landing page of the platform (no login). Logged-out visitors to any
// student URL are sent here by the `_app` guard; sign-in and sign-up are
// Google-only (Auth0 connection `google-oauth2`). Portuguese is the product
// language, so the browser's Portuguese preference wins for anonymous visitors
// (same as /journal). The page sells the whole school (Autônoma, em Grupo,
// Jornadas com Propósito); only the Autônoma journey is bought here, via the
// base-access product (price + trial read live from `access.publicBase`).

const API_URL = import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:3333/api';

// School links (public, from english2empower.com).
const LINKS = {
  site: 'https://english2empower.com/',
  form: 'https://english2empower.com/formulario/',
  whatsapp: 'https://wa.me/5541996038223',
  instagram: 'https://www.instagram.com/english2empower/',
  linkedin: 'https://www.linkedin.com/company/english2empower/',
};

const montserrat = "'Montserrat', sans-serif";

export const Route = createFileRoute('/welcome')({
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
  }),
  component: WelcomePage,
});

// ---------------------------------------------------------------------------
// Styles. Class-based (not inline) because the page needs hover, media queries,
// keyframes and reduced-motion handling. Everything is prefixed `wl-`.
// ---------------------------------------------------------------------------
const WELCOME_STYLES = `
html { scroll-behavior: smooth; }
.wl section[id] { scroll-margin-top: 84px; }
.wl { font-family: ${montserrat}; color: ${P.darkText}; background: ${P.cream}; overflow-x: clip; }
.wl *, .wl *::before, .wl *::after { box-sizing: border-box; }
.wl-wrap { max-width: 1180px; margin: 0 auto; padding: 0 24px; }
.wl-eyebrow { font-family: ${oswald}; font-weight: 600; font-size: 12px; letter-spacing: .22em; text-transform: uppercase; color: ${P.maxRed}; margin: 0 0 12px; }
.wl-h2 { font-family: ${dmSerif}; font-weight: 400; font-size: clamp(30px, 3.6vw, 46px); line-height: 1.08; letter-spacing: -.01em; margin: 0 0 16px; color: ${P.darkText}; text-wrap: balance; }
.wl-sub { font-size: 17px; line-height: 1.65; color: ${P.midText}; margin: 0; max-width: 58ch; }
.wl .wl-btn { -webkit-appearance: none; appearance: none; font: inherit; display: inline-flex; align-items: center; gap: 10px; font-family: ${oswald}; font-weight: 600; font-size: 14px; letter-spacing: .12em; text-transform: uppercase; height: auto; padding: 14px 22px; border-radius: 4px; border: 2px solid ${P.black}; line-height: 1.2; cursor: pointer; text-decoration: none; transition: transform .15s ease, filter .15s ease, background .15s ease; white-space: nowrap; }
.wl .wl-btn:hover { filter: brightness(1.06); transform: translateY(-1px); }
.wl .wl-btn:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.wl .wl-btn-red { background: ${P.maxRed}; border-color: ${P.maxRed}; color: ${P.white}; }
.wl .wl-btn-black { background: ${P.black}; border-color: ${P.black}; color: ${P.white}; }
.wl .wl-btn-ghost { background: transparent; border-color: ${P.black}; color: ${P.black}; }
.wl .wl-btn-white { background: ${P.white}; border-color: ${P.white}; color: ${P.maxRed}; }
.wl .wl-btn-outline-white { background: transparent; border-color: rgba(255,255,255,.7); color: ${P.white}; }
.wl .wl-btn-sm { padding: 10px 16px; font-size: 12px; }

/* top bar */
.wl-nav { position: sticky; top: 0; z-index: 40; transition: background .25s ease, box-shadow .25s ease, backdrop-filter .25s ease; }
.wl-nav.is-scrolled { background: rgba(255,248,245,.9); backdrop-filter: blur(10px); box-shadow: 0 1px 0 ${P.lightBorder}; }
.wl-nav-in { display: flex; align-items: center; justify-content: space-between; gap: 16px; height: 68px; }
.wl-nav-links { display: flex; gap: 26px; }
.wl-nav-links a { font-family: ${oswald}; font-weight: 500; font-size: 13px; letter-spacing: .14em; text-transform: uppercase; color: ${P.darkText}; text-decoration: none; padding: 6px 0; border-bottom: 2px solid transparent; }
.wl-nav-links a:hover { border-bottom-color: ${P.maxRed}; }
.wl-nav-actions { display: flex; align-items: center; gap: 10px; }
.wl-lang { font-family: ${oswald}; font-size: 12px; letter-spacing: .14em; text-transform: uppercase; background: none; border: 0; color: ${P.midText}; cursor: pointer; padding: 6px 8px; }
.wl-lang:hover { color: ${P.maxRed}; }
.wl-link:focus-visible, .wl-lang:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }

/* hero */
.wl-hero { position: relative; padding: 36px 0 0; overflow: hidden; }
.wl-hero-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr); gap: 24px; align-items: center; }
.wl-hero-copy { padding: 24px 0 56px; position: relative; z-index: 2; }
.wl-hero-title { font-family: ${dmSerif}; font-weight: 400; font-size: clamp(42px, 5.4vw, 74px); line-height: 1.0; letter-spacing: -.015em; margin: 0 0 22px; color: ${P.darkText}; }
.wl-hero-title em { font-style: italic; color: ${P.maxRed}; }
.wl-hero-sub { font-size: 18px; line-height: 1.6; color: ${P.midText}; margin: 0 0 28px; max-width: 46ch; }
.wl-hero-ctas { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 14px; }
.wl-hero-fine { font-size: 13px; color: ${P.lightText}; margin: 0; line-height: 1.6; }
.wl-hero-fine b { color: ${P.darkText}; font-weight: 600; }
.wl-hero-art { position: relative; margin-right: calc(-1 * max(24px, (100vw - 1180px) / 2)); min-height: 320px; }
.wl-hero-img { display: block; width: 110%; margin-left: -10%; max-width: none; height: auto; will-change: transform;
  -webkit-mask-image: linear-gradient(to right, transparent 0, #000 9%, #000 100%), linear-gradient(to bottom, #000 0, #000 86%, transparent 100%);
  -webkit-mask-composite: source-in; mask-image: linear-gradient(to right, transparent 0, #000 9%, #000 100%), linear-gradient(to bottom, #000 0, #000 86%, transparent 100%); mask-composite: intersect; }
.wl-tape { position: absolute; height: 26px; width: 110px; background: rgba(242,160,196,.55); mix-blend-mode: multiply; box-shadow: 0 1px 2px rgba(120,40,40,.08); pointer-events: none; }
.wl-tape::after { content: ""; position: absolute; inset: 0; background: repeating-linear-gradient(90deg, rgba(255,255,255,.18) 0 2px, transparent 2px 6px); }

/* entrance + reveal */
@keyframes wl-rise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
.wl-rise { animation: wl-rise .7s cubic-bezier(.2,.7,.2,1) both; }
.wl-rise-2 { animation-delay: .12s; } .wl-rise-3 { animation-delay: .24s; } .wl-rise-4 { animation-delay: .36s; }
[data-reveal] { opacity: 0; transform: translateY(26px); transition: opacity .7s cubic-bezier(.2,.7,.2,1), transform .7s cubic-bezier(.2,.7,.2,1); }
[data-reveal].is-in { opacity: 1; transform: none; }
[data-reveal="2"] { transition-delay: .1s; } [data-reveal="3"] { transition-delay: .2s; } [data-reveal="4"] { transition-delay: .3s; }

/* manifesto */
.wl-manifesto { background: ${P.black}; color: ${P.cream}; padding: 96px 0; position: relative; overflow: hidden; }
.wl-manifesto .wl-eyebrow { color: ${P.charm}; }
.wl-manifesto-lead { font-family: ${dmSerif}; font-size: clamp(26px, 3.3vw, 42px); line-height: 1.22; margin: 0 0 28px; max-width: 24ch; text-wrap: pretty; }
.wl-manifesto-body { font-size: 18px; line-height: 1.7; color: #E8CFD3; max-width: 56ch; margin: 0; }
.wl-manifesto-body em { font-family: ${dmSerif}; font-style: italic; color: ${P.charm}; font-size: 1.15em; }
.wl-manifesto-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: end; }
.wl-seal { width: 148px; height: 148px; border-radius: 50%; background: ${P.maxRed}; color: ${P.white}; display: grid; place-items: center; text-align: center; font-family: ${oswald}; font-weight: 700; font-size: 13px; letter-spacing: .16em; text-transform: uppercase; line-height: 1.3; padding: 18px; box-shadow: 0 0 0 6px ${P.black}, 0 0 0 8px rgba(242,160,196,.6); transform: rotate(-8deg); justify-self: end; transition: transform .3s ease; }
.wl-seal:hover { transform: rotate(4deg) scale(1.04); }
.wl-seal img { width: 34px; height: 34px; display: block; margin: 0 auto 8px; }

/* method list */
.wl-method { padding: 96px 0 80px; }
.wl-method-head { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: end; margin-bottom: 40px; }
.wl-list { border-top: 1px solid ${P.darkText}; }
.wl-row { display: grid; grid-template-columns: 72px 1fr auto; gap: 24px; align-items: start; padding: 28px 12px; border-bottom: 1px solid ${P.lightBorder}; transition: background .2s ease; }
.wl-row:hover { background: ${P.blush}; }
.wl-num { font-family: ${oswald}; font-weight: 700; font-size: 34px; color: ${P.maxRed}; line-height: 1; letter-spacing: .02em; }
.wl-row h3 { font-family: ${oswald}; font-weight: 600; font-size: 20px; letter-spacing: .04em; text-transform: uppercase; margin: 4px 0 8px; color: ${P.darkText}; }
.wl-row p { margin: 0; font-size: 15.5px; line-height: 1.6; color: ${P.midText}; max-width: 62ch; }
.wl-pill { font-family: ${oswald}; font-weight: 600; font-size: 11px; letter-spacing: .18em; text-transform: uppercase; border: 1.5px solid ${P.darkText}; border-radius: 999px; padding: 6px 12px; color: ${P.darkText}; white-space: nowrap; margin-top: 6px; }

/* platform */
.wl-platform { padding: 40px 0 96px; overflow: hidden; }
.wl-platform-grid { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 0.9fr); gap: 40px; align-items: center; }
.wl-platform-art { margin-left: calc(-1 * max(24px, (100vw - 1180px) / 2)); position: relative; }
.wl-platform-img { display: block; width: 106%; max-width: none; height: auto; margin-left: -3%;
  -webkit-mask-image: linear-gradient(to left, transparent 0, #000 10%, #000 100%); mask-image: linear-gradient(to left, transparent 0, #000 10%, #000 100%); }
.wl-steps { display: grid; gap: 16px; }
.wl-step { position: relative; background: ${P.white}; border: 1px solid ${P.lightBorder}; padding: 20px 22px 20px 24px; box-shadow: 6px 10px 22px ${P.shadow}; transition: transform .2s ease; }
.wl-step:nth-child(1) { transform: rotate(-.8deg); } .wl-step:nth-child(2) { transform: rotate(.6deg); margin-left: 18px; } .wl-step:nth-child(3) { transform: rotate(-.4deg); }
.wl-step:hover { transform: rotate(0) translateY(-2px); }
.wl-step .wl-tape { top: -12px; left: 50%; width: 92px; height: 22px; transform: translateX(-50%) rotate(-3deg); }
.wl-step-k { font-family: ${oswald}; font-weight: 600; font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: ${P.maxRed}; }
.wl-step h3 { font-family: ${oswald}; font-weight: 600; font-size: 18px; letter-spacing: .03em; text-transform: uppercase; margin: 4px 0 6px; }
.wl-step p { margin: 0; font-size: 14.5px; line-height: 1.55; color: ${P.midText}; }

/* community */
.wl-community { background: ${P.baby}; padding: 88px 0; }
.wl-community-grid { display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 44px; border-top: 1px solid rgba(45,26,31,.25); }
.wl-ticket { padding: 28px 28px 28px 0; border-right: 1px dashed rgba(45,26,31,.3); }
.wl-ticket + .wl-ticket { padding-left: 28px; }
.wl-ticket:last-child { border-right: 0; }
.wl-ticket-k { font-family: ${oswald}; font-weight: 700; font-size: 22px; letter-spacing: .02em; text-transform: uppercase; color: ${P.maxRed}; margin: 0 0 4px; line-height: 1.1; }
.wl-ticket h3 { font-family: ${dmSerif}; font-weight: 400; font-size: 20px; margin: 0 0 10px; color: ${P.darkText}; }
.wl-ticket p { margin: 0; font-size: 15px; line-height: 1.6; color: ${P.midText}; }

/* plans */
.wl-plans { padding: 96px 0 80px; }
.wl-plans-grid { display: grid; grid-template-columns: 1fr 1.1fr; gap: 28px; align-items: stretch; margin-top: 44px; }
.wl-plan { position: relative; padding: 34px 34px 30px; display: flex; flex-direction: column; gap: 18px; }
.wl-plan-light { background: ${P.white}; border: 1px solid ${P.lightBorder}; box-shadow: 6px 10px 22px ${P.shadow}; }
.wl-plan-dark { background: ${P.black}; color: ${P.white}; box-shadow: 10px 16px 34px ${P.shadowDeep}; }
.wl-plan .wl-tape { top: -13px; left: 28px; transform: rotate(-4deg); }
.wl-plan-tag { font-family: ${oswald}; font-weight: 600; font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: ${P.maxRed}; }
.wl-plan-dark .wl-plan-tag { color: ${P.charm}; }
.wl-plan h3 { font-family: ${oswald}; font-weight: 700; font-size: 26px; letter-spacing: .03em; text-transform: uppercase; margin: 0; line-height: 1.1; }
.wl-plan-desc { margin: 0; font-size: 15px; line-height: 1.6; color: ${P.midText}; }
.wl-plan-dark .wl-plan-desc { color: #E8CFD3; }
.wl-price { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.wl-price-big { font-family: ${oswald}; font-weight: 700; font-size: 48px; line-height: 1; letter-spacing: -.01em; color: ${P.darkText}; }
.wl-plan-dark .wl-price-big { color: ${P.white}; }
.wl-price-unit { font-family: ${oswald}; font-size: 16px; color: ${P.midText}; letter-spacing: .04em; }
.wl-plan-dark .wl-price-unit { color: #E8CFD3; }
.wl-price-trial { font-family: ${oswald}; font-weight: 600; font-size: 12px; letter-spacing: .14em; text-transform: uppercase; background: ${P.baby}; color: ${P.maxRed}; padding: 6px 10px; border-radius: 999px; }
.wl-price-rows { display: grid; gap: 10px; }
.wl-price-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding-bottom: 10px; border-bottom: 1px dashed rgba(255,255,255,.2); }
.wl-price-row span:first-child { font-family: ${oswald}; font-weight: 500; font-size: 14px; letter-spacing: .1em; text-transform: uppercase; color: #E8CFD3; }
.wl-price-row small { display: block; font-family: ${montserrat}; text-transform: none; letter-spacing: 0; font-size: 12px; color: ${P.charm}; }
.wl-price-row b { font-family: ${oswald}; font-weight: 700; font-size: 24px; white-space: nowrap; }
.wl-features { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
.wl-features li { display: flex; gap: 10px; align-items: flex-start; font-size: 15px; line-height: 1.5; }
.wl-features svg { flex: none; margin-top: 3px; }
.wl-features li.is-not { color: ${P.lightText}; }
.wl-plan-dark .wl-features li.is-not { color: #B99AA2; }
.wl-plan-ctas { display: flex; flex-wrap: wrap; gap: 10px; margin-top: auto; padding-top: 6px; }
.wl-enroll { font-family: ${oswald}; font-weight: 600; font-size: 12px; letter-spacing: .16em; text-transform: uppercase; color: ${P.charm}; }
.wl-purpose { margin-top: 28px; background: ${P.baby}; display: grid; grid-template-columns: 1fr auto; gap: 28px; align-items: center; padding: 30px 34px; position: relative; }
.wl-purpose h3 { font-family: ${oswald}; font-weight: 700; font-size: 22px; letter-spacing: .03em; text-transform: uppercase; margin: 0 0 6px; }
.wl-purpose p { margin: 0; font-size: 15px; line-height: 1.6; color: ${P.midText}; max-width: 70ch; }
.wl-purpose-price { font-family: ${oswald}; font-weight: 700; font-size: 20px; color: ${P.maxRed}; margin: 0 0 10px; }
.wl-footnote { margin: 22px 0 0; font-size: 13px; color: ${P.lightText}; line-height: 1.6; }
.wl-footnote a { color: ${P.midText}; }

/* founder */
.wl-founder { padding: 40px 0 96px; }
.wl-founder-grid { display: grid; grid-template-columns: minmax(0, 0.95fr) minmax(0, 1fr); gap: 56px; align-items: center; }
.wl-founder-art { position: relative; transform: rotate(-1.5deg); }
.wl-founder-art img { display: block; width: 100%; height: auto; background: ${P.white}; border: 10px solid ${P.white}; box-shadow: 10px 16px 34px ${P.shadowDeep}; }
.wl-founder-art .wl-tape { top: -12px; right: 36px; transform: rotate(6deg); }
.wl-founder-art .wl-tape.b { top: auto; right: auto; bottom: -12px; left: 40px; transform: rotate(-5deg); }
.wl-founder-role { font-family: ${oswald}; font-weight: 500; font-size: 14px; letter-spacing: .08em; text-transform: uppercase; color: ${P.midText}; margin: 0 0 20px; }
.wl-quote { font-family: ${dmSerif}; font-style: italic; font-size: clamp(22px, 2.4vw, 30px); line-height: 1.25; color: ${P.maxRed}; margin: 28px 0 0; padding-left: 18px; border-left: 3px solid ${P.maxRed}; }

/* final band */
.wl-final { background: ${P.maxRed}; color: ${P.white}; padding: 96px 0; position: relative; overflow: hidden; }
.wl-final .wl-h2 { color: ${P.white}; font-size: clamp(34px, 4.6vw, 60px); max-width: 16ch; }
.wl-final .wl-sub { color: #FBE0E3; }
.wl-final-ctas { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 30px; }
.wl-final-mark { position: absolute; right: -40px; bottom: -60px; width: 360px; opacity: .12; pointer-events: none; }

/* footer */
.wl-footer { background: ${P.black}; color: #E8CFD3; padding: 44px 0 110px; font-size: 13px; }
.wl-footer-grid { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 24px; align-items: flex-start; }
.wl-footer-links { display: flex; flex-wrap: wrap; gap: 18px; }
.wl-footer a { color: ${P.white}; text-decoration: none; font-family: ${oswald}; font-size: 12px; letter-spacing: .14em; text-transform: uppercase; display: inline-flex; align-items: center; gap: 6px; }
.wl-footer a:hover { color: ${P.charm}; }
.wl-footer-fine { margin: 26px 0 0; color: #B99AA2; line-height: 1.6; max-width: 70ch; }

/* floating WhatsApp */
.wl-whats { position: fixed; right: 20px; bottom: 20px; z-index: 50; display: inline-flex; align-items: center; gap: 10px; background: ${P.black}; color: ${P.white}; border-radius: 999px; padding: 12px 18px 12px 14px; text-decoration: none; font-family: ${oswald}; font-weight: 600; font-size: 13px; letter-spacing: .1em; text-transform: uppercase; box-shadow: 0 10px 24px rgba(26,16,24,.28); transition: transform .2s ease, background .2s ease; }
.wl-whats:hover { transform: translateY(-2px); background: #25D366; color: ${P.black}; }
.wl-whats:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.wl-whats svg { width: 22px; height: 22px; }

/* responsive */
@media (max-width: 960px) {
  .wl-hero-grid, .wl-platform-grid, .wl-founder-grid, .wl-manifesto-grid, .wl-method-head { grid-template-columns: 1fr; }
  .wl-hero-copy { padding-bottom: 8px; }
  .wl-hero-art { margin: 0 calc(50% - 50vw) 0; }
  .wl-hero-img { width: 100%; margin-left: 0; -webkit-mask-image: linear-gradient(to bottom, #000 0, #000 80%, transparent 100%); mask-image: linear-gradient(to bottom, #000 0, #000 80%, transparent 100%); }
  .wl-platform-art { margin: 0 calc(50% - 50vw); }
  .wl-platform-img { width: 100%; margin-left: 0; -webkit-mask-image: none; mask-image: none; }
  .wl-seal { justify-self: start; }
  .wl-community-grid { grid-template-columns: 1fr; }
  .wl-ticket { border-right: 0; border-bottom: 1px dashed rgba(45,26,31,.3); padding: 24px 0; }
  .wl-ticket + .wl-ticket { padding-left: 0; }
  .wl-ticket:last-child { border-bottom: 0; }
  .wl-plans-grid { grid-template-columns: 1fr; }
  .wl-purpose { grid-template-columns: 1fr; }
  .wl-founder-art { max-width: 520px; margin: 0 auto; }
}
@media (max-width: 480px) { .wl .wl-nav-login { display: none; } }
@media (max-width: 640px) {
  .wl-nav-links { display: none; }
  .wl-nav-in { height: 60px; }
  .wl-hero { padding-top: 20px; }
  .wl-row { grid-template-columns: 48px 1fr; }
  .wl-row .wl-pill { display: none; }
  .wl-num { font-size: 26px; }
  .wl-plan { padding: 28px 22px 24px; }
  .wl-purpose { padding: 24px 22px; }
  .wl-step:nth-child(2) { margin-left: 0; }
  .wl-whats span { display: none; }
  .wl-whats { padding: 12px; }
  .wl-manifesto, .wl-method, .wl-plans, .wl-final, .wl-community { padding-top: 64px; padding-bottom: 64px; }
}
@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto; }
  .wl-rise, [data-reveal] { animation: none; opacity: 1; transform: none; transition: none; }
  .wl-hero-img { transform: none !important; }
  .wl-btn, .wl-step, .wl-seal, .wl-whats { transition: none; }
}
`;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
function WelcomePage() {
  const { t, i18n } = useTranslation('p2e');
  const { isLoggedIn, isLoading } = useAuth();
  const { loginWithRedirect } = useAuth0();
  const { returnTo } = Route.useSearch();
  const [base, setBase] = useState<PublicBase | null | undefined>(undefined);
  const [scrolled, setScrolled] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const heroImgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!isLoggedIn && i18n.language === 'en' && navigator.language?.toLowerCase().startsWith('pt')) {
      void i18n.changeLanguage('pt');
    }
  }, [isLoggedIn, i18n]);

  useEffect(() => {
    const ctrl = new AbortController();
    void fetchPublicBase(API_URL, ctrl.signal).then(setBase);
    return () => ctrl.abort();
  }, []);

  // Sticky bar state + gentle hero parallax (skipped for reduced motion).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        setScrolled(y > 24);
        if (!reduce && heroImgRef.current) {
          heroImgRef.current.style.transform = `translateY(${Math.min(y, 900) * -0.08}px)`;
        }
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  // Scroll reveal for every [data-reveal] block.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') {
      root?.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
    );
    root.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  if (!isLoading && isLoggedIn) {
    return <Navigate to={sanitizeReturnTo(returnTo) ?? '/'} replace />;
  }

  const go = (signup: boolean) => {
    const target = sanitizeReturnTo(returnTo) ?? '/';
    rememberReturnTo(typeof window !== 'undefined' ? window.sessionStorage : null, target);
    void loginWithRedirect({
      authorizationParams: { connection: 'google-oauth2', ...(signup ? { screen_hint: 'signup' } : {}) },
      appState: { returnTo: target },
    });
  };

  const lang = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const toggleLang = () => void i18n.changeLanguage(lang === 'pt' ? 'en' : 'pt');
  const priceLabel = base ? formatPrice(base.priceCents, base.currency, i18n.language ?? 'pt') : null;
  const trialDays = base?.trialDays || 7;
  const year = new Date().getFullYear();

  const methodItems = [1, 2, 3, 4] as const;
  const steps = [1, 2, 3] as const;
  const clubs = ['conversations', 'readings', 'workshops'] as const;

  return (
    <div className="wl" ref={rootRef} style={{ minHeight: '100svh' }}>
      <style dangerouslySetInnerHTML={{ __html: WELCOME_STYLES }} />

      {/* ---------------------------------------------------------------- nav */}
      <header className={`wl-nav${scrolled ? ' is-scrolled' : ''}`}>
        <div className="wl-wrap wl-nav-in">
          <a href="#top" className="wl-link" aria-label="English2Empower" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/logo-small.png" alt="English2Empower" style={{ height: 30, width: 'auto', display: 'block' }} />
          </a>
          <nav className="wl-nav-links" aria-label={t('welcome.nav.aria')}>
            <a href="#metodo">{t('welcome.nav.method')}</a>
            <a href="#plataforma">{t('welcome.nav.platform')}</a>
            <a href="#comunidade">{t('welcome.nav.community')}</a>
            <a href="#jornadas">{t('welcome.nav.plans')}</a>
          </nav>
          <div className="wl-nav-actions">
            <button type="button" className="wl-lang" onClick={toggleLang} aria-label={t('welcome.nav.langAria')}>
              {lang === 'pt' ? 'EN' : 'PT'}
            </button>
            <button type="button" className="wl-btn wl-btn-ghost wl-btn-sm wl-nav-login" onClick={() => go(false)} data-testid="welcome-login">
              {t('welcome.nav.login')}
            </button>
            <button type="button" className="wl-btn wl-btn-red wl-btn-sm" onClick={() => go(true)} data-testid="welcome-signup">
              {t('welcome.nav.start')}
            </button>
          </div>
        </div>
      </header>

      {/* --------------------------------------------------------------- hero */}
      <section className="wl-hero" id="top" data-testid="welcome-cta">
        <div className="wl-wrap">
          <div className="wl-hero-grid">
            <div className="wl-hero-copy">
              <p className="wl-eyebrow wl-rise">{t('welcome.hero.eyebrow')}</p>
              <h1 className="wl-hero-title wl-rise wl-rise-2">
                {t('welcome.hero.title1')}
                <br />
                <em>{t('welcome.hero.title2')}</em>
              </h1>
              <p className="wl-hero-sub wl-rise wl-rise-3">{t('welcome.hero.sub')}</p>
              <div className="wl-hero-ctas wl-rise wl-rise-4">
                <button type="button" className="wl-btn wl-btn-red" onClick={() => go(true)}>
                  {t('welcome.hero.ctaTrial', { count: trialDays })}
                  <ArrowRight size={16} aria-hidden />
                </button>
                <button type="button" className="wl-btn wl-btn-ghost" onClick={() => go(false)}>
                  {t('welcome.hero.ctaLogin')}
                </button>
              </div>
              <p className="wl-hero-fine wl-rise wl-rise-4">
                {priceLabel && <b>{t('welcome.hero.priceAfter', { price: priceLabel })} </b>}
                {t('welcome.hero.googleNote')}
              </p>
            </div>
            <div className="wl-hero-art wl-rise wl-rise-2">
              <img ref={heroImgRef} className="wl-hero-img" src={heroImg} alt={t('welcome.hero.imageAlt')} {...{ fetchpriority: "high" }} />
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- manifesto */}
      <section className="wl-manifesto">
        <div className="wl-wrap wl-manifesto-grid">
          <div data-reveal>
            <p className="wl-eyebrow">{t('welcome.manifesto.eyebrow')}</p>
            <p className="wl-manifesto-lead">{t('welcome.manifesto.lead')}</p>
            <p className="wl-manifesto-body">
              {t('welcome.manifesto.body')} <em>{t('welcome.manifesto.highlight')}</em>
            </p>
          </div>
          <div className="wl-seal" data-reveal="2" aria-hidden>
            <div>
              <img src="/icon-hand-small-white.png" alt="" />
              {t('welcome.manifesto.seal')}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- method */}
      <section className="wl-method" id="metodo">
        <div className="wl-wrap">
          <div className="wl-method-head" data-reveal>
            <div>
              <p className="wl-eyebrow">{t('welcome.method.eyebrow')}</p>
              <h2 className="wl-h2">{t('welcome.method.title')}</h2>
            </div>
            <p className="wl-sub">{t('welcome.method.sub')}</p>
          </div>
          <ol className="wl-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {methodItems.map((n) => (
              <li key={n} className="wl-row" data-reveal>
                <span className="wl-num" aria-hidden>
                  0{n}
                </span>
                <div>
                  <h3>{t(`welcome.method.items.${n}.title`)}</h3>
                  <p>{t(`welcome.method.items.${n}.text`)}</p>
                </div>
                <span className="wl-pill">{t(`welcome.method.items.${n}.tag`)}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ----------------------------------------------------------- platform */}
      <section className="wl-platform" id="plataforma">
        <div className="wl-wrap">
          <div className="wl-platform-grid">
            <div className="wl-platform-art" data-reveal>
              <img className="wl-platform-img" src={platformImg} alt={t('welcome.platform.imageAlt')} loading="lazy" />
            </div>
            <div>
              <div data-reveal>
                <p className="wl-eyebrow">{t('welcome.platform.eyebrow')}</p>
                <h2 className="wl-h2">{t('welcome.platform.title')}</h2>
                <p className="wl-sub" style={{ marginBottom: 32 }}>
                  {t('welcome.platform.sub')}
                </p>
              </div>
              <div className="wl-steps">
                {steps.map((n) => (
                  <article key={n} className="wl-step" data-reveal={String(n)}>
                    <span className="wl-tape" aria-hidden />
                    <span className="wl-step-k">{t('welcome.platform.stepLabel', { n })}</span>
                    <h3>{t(`welcome.platform.steps.${n}.title`)}</h3>
                    <p>{t(`welcome.platform.steps.${n}.text`)}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- community */}
      <section className="wl-community" id="comunidade">
        <div className="wl-wrap">
          <div data-reveal>
            <p className="wl-eyebrow">{t('welcome.community.eyebrow')}</p>
            <h2 className="wl-h2">{t('welcome.community.title')}</h2>
            <p className="wl-sub">{t('welcome.community.sub')}</p>
          </div>
          <div className="wl-community-grid">
            {clubs.map((k, i) => (
              <div key={k} className="wl-ticket" data-reveal={String(i + 1)}>
                <p className="wl-ticket-k">{t(`welcome.community.items.${k}.name`)}</p>
                <h3>{t(`welcome.community.items.${k}.kind`)}</h3>
                <p>{t(`welcome.community.items.${k}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------------- plans */}
      <section className="wl-plans" id="jornadas">
        <div className="wl-wrap">
          <div data-reveal>
            <p className="wl-eyebrow">{t('welcome.plans.eyebrow')}</p>
            <h2 className="wl-h2">{t('welcome.plans.title')}</h2>
            <p className="wl-sub">{t('welcome.plans.sub')}</p>
          </div>

          <div className="wl-plans-grid">
            {/* Autônoma: the one you can start here */}
            <article className="wl-plan wl-plan-light" data-reveal data-testid="welcome-plan-autonoma">
              <span className="wl-tape" aria-hidden />
              <span className="wl-plan-tag">{t('welcome.plans.autonoma.tag')}</span>
              <h3>{t('welcome.plans.autonoma.name')}</h3>
              <p className="wl-plan-desc">{t('welcome.plans.autonoma.desc')}</p>
              <div className="wl-price">
                {priceLabel ? (
                  <>
                    <span className="wl-price-big">{priceLabel}</span>
                    <span className="wl-price-unit">{t('welcome.plans.autonoma.perMonth')}</span>
                  </>
                ) : (
                  <span className="wl-price-unit">{base === undefined ? t('welcome.plans.autonoma.priceLoading') : t('welcome.plans.autonoma.priceSoon')}</span>
                )}
                <span className="wl-price-trial">{t('welcome.plans.autonoma.trial', { count: trialDays })}</span>
              </div>
              <FeatureList items={[1, 2, 3].map((n) => t(`welcome.plans.autonoma.features.${n}`))} not={[t('welcome.plans.autonoma.not')]} />
              <div className="wl-plan-ctas">
                <button type="button" className="wl-btn wl-btn-red" onClick={() => go(true)}>
                  {t('welcome.plans.autonoma.cta')}
                  <ArrowRight size={16} aria-hidden />
                </button>
              </div>
            </article>

            {/* em Grupo: the core, sold via the pre-enrollment form */}
            <article className="wl-plan wl-plan-dark" data-reveal="2" data-testid="welcome-plan-grupo">
              <span className="wl-plan-tag">{t('welcome.plans.grupo.tag')}</span>
              <h3>{t('welcome.plans.grupo.name')}</h3>
              <p className="wl-plan-desc">{t('welcome.plans.grupo.desc')}</p>
              <div className="wl-price-rows">
                <div className="wl-price-row">
                  <span>{t('welcome.plans.grupo.extensivo')}</span>
                  <b>{t('welcome.plans.grupo.extensivoPrice')}</b>
                </div>
                <div className="wl-price-row">
                  <span>
                    {t('welcome.plans.grupo.semi')}
                    <small>{t('welcome.plans.grupo.semiNote')}</small>
                  </span>
                  <b>{t('welcome.plans.grupo.semiPrice')}</b>
                </div>
              </div>
              <FeatureList items={[1, 2, 3].map((n) => t(`welcome.plans.grupo.features.${n}`))} dark />
              <span className="wl-enroll">{t('welcome.plans.grupo.enroll')}</span>
              <div className="wl-plan-ctas">
                <a className="wl-btn wl-btn-white" href={LINKS.form} target="_blank" rel="noopener noreferrer">
                  {t('welcome.plans.grupo.ctaForm')}
                  <ArrowRight size={16} aria-hidden />
                </a>
                <a className="wl-btn wl-btn-outline-white" href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon size={16} />
                  {t('welcome.plans.grupo.ctaWhats')}
                </a>
              </div>
            </article>
          </div>

          {/* Jornadas com Propósito */}
          <article className="wl-purpose" data-reveal data-testid="welcome-plan-proposito">
            <div>
              <span className="wl-plan-tag">{t('welcome.plans.proposito.tag')}</span>
              <h3>{t('welcome.plans.proposito.name')}</h3>
              <p className="wl-purpose-price">{t('welcome.plans.proposito.price')}</p>
              <p>{t('welcome.plans.proposito.desc')}</p>
            </div>
            <button type="button" className="wl-btn wl-btn-ghost" onClick={() => go(true)}>
              {t('welcome.plans.proposito.cta')}
            </button>
          </article>

          <p className="wl-footnote" data-reveal>
            {t('welcome.plans.womenLeaders')}{' '}
            <a href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer" className="wl-link">
              {t('welcome.plans.womenLeadersLink')}
            </a>
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------------ founder */}
      <section className="wl-founder" id="fundadora">
        <div className="wl-wrap wl-founder-grid">
          <div className="wl-founder-art" data-reveal>
            <span className="wl-tape" aria-hidden />
            <span className="wl-tape b" aria-hidden />
            <img src={founderImg} alt={t('welcome.founder.imageAlt')} loading="lazy" />
          </div>
          <div data-reveal="2">
            <p className="wl-eyebrow">{t('welcome.founder.eyebrow')}</p>
            <h2 className="wl-h2">{t('welcome.founder.name')}</h2>
            <p className="wl-founder-role">{t('welcome.founder.role')}</p>
            <p className="wl-sub">{t('welcome.founder.body')}</p>
            <blockquote className="wl-quote">{t('welcome.founder.quote')}</blockquote>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------------- final */}
      <section className="wl-final">
        <img className="wl-final-mark" src="/icon-hand-small-white.png" alt="" aria-hidden />
        <div className="wl-wrap" data-reveal>
          <h2 className="wl-h2">{t('welcome.final.title')}</h2>
          <p className="wl-sub">{t('welcome.final.sub')}</p>
          <div className="wl-final-ctas">
            <button type="button" className="wl-btn wl-btn-white" onClick={() => go(true)}>
              {t('welcome.hero.ctaTrial', { count: trialDays })}
              <ArrowRight size={16} aria-hidden />
            </button>
            <a className="wl-btn wl-btn-outline-white" href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon size={16} />
              {t('welcome.final.ctaWhats')}
            </a>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- footer */}
      <footer className="wl-footer">
        <div className="wl-wrap">
          <div className="wl-footer-grid">
            <div style={{ fontFamily: oswald, fontWeight: 700, fontSize: 16, letterSpacing: '.06em', color: P.white, textTransform: 'uppercase' }}>
              English2Empower
            </div>
            <div className="wl-footer-links">
              <a href="#metodo">{t('welcome.nav.method')}</a>
              <a href="#plataforma">{t('welcome.nav.platform')}</a>
              <a href="#comunidade">{t('welcome.nav.community')}</a>
              <a href="#jornadas">{t('welcome.nav.plans')}</a>
            </div>
            <div className="wl-footer-links">
              <a href={LINKS.site} target="_blank" rel="noopener noreferrer">
                {t('welcome.footer.site')}
              </a>
              <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer">
                <Instagram size={14} aria-hidden /> Instagram
              </a>
              <a href={LINKS.linkedin} target="_blank" rel="noopener noreferrer">
                <Linkedin size={14} aria-hidden /> LinkedIn
              </a>
              <a href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer">
                <WhatsAppIcon size={14} /> WhatsApp
              </a>
            </div>
          </div>
          <p className="wl-footer-fine">
            {t('welcome.footer.copyright', { year })} {t('welcome.footer.privacy')}
          </p>
        </div>
      </footer>

      {/* floating WhatsApp */}
      <a className="wl-whats" href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer" aria-label={t('welcome.whats.aria')}>
        <WhatsAppIcon size={22} />
        <span>{t('welcome.whats.label')}</span>
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------
function FeatureList({ items, not = [], dark = false }: { items: string[]; not?: string[]; dark?: boolean }) {
  const ok = dark ? P.charm : P.green;
  return (
    <ul className="wl-features">
      {items.map((s) => (
        <li key={s}>
          <Check size={16} color={ok} aria-hidden />
          <span>{s}</span>
        </li>
      ))}
      {not.map((s) => (
        <li key={s} className="is-not">
          <Minus size={16} aria-hidden />
          <span>{s}</span>
        </li>
      ))}
    </ul>
  );
}

// lucide has no WhatsApp glyph; this is the standard logo path.
function WhatsAppIcon({ size = 16, style }: { size?: number; style?: CSSProperties }): ReactNode {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden style={style}>
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.91-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.05 21.8h-.01a9.8 9.8 0 0 1-4.99-1.37l-.36-.21-3.71.97.99-3.62-.23-.37a9.78 9.78 0 0 1-1.5-5.22c0-5.41 4.4-9.81 9.82-9.81 2.62 0 5.08 1.02 6.93 2.88a9.75 9.75 0 0 1 2.87 6.94c0 5.41-4.41 9.81-9.81 9.81zm8.35-18.16A11.76 11.76 0 0 0 12.05 0C5.5 0 .17 5.33.17 11.88c0 2.09.55 4.14 1.59 5.94L.07 24l6.33-1.66a11.9 11.9 0 0 0 5.65 1.44h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.24-6.16-3.54-8.26z" />
    </svg>
  );
}
